import { RefObject, useRef, useEffect, useState } from 'react';
import ViewObserver, { DEFAULT_OPTIONS } from '~/components/view-tracker/view-observer';

interface UseViewTrackingProps<E> {
  ref?: RefObject<E>;
  onView: () => void;
}

export const useViewTracking = <E extends HTMLElement = HTMLDivElement>(
  props: UseViewTrackingProps<E>,
  deps: unknown[] = [],
): RefObject<E> => {
  const { onView, ref: inputRef } = props;
  const ref = inputRef || useRef<E>(null);
  const [tracked, setTracked] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || tracked) {
      return;
    }

    const observer = ViewObserver.get(DEFAULT_OPTIONS);
    observer.observe(element, (visible: boolean) => {
      if (visible) {
        onView();
        setTracked(true);
        observer.unobserve(element);
      }
    });

    return () => {
      observer.unobserve(element);
    };
  }, [ref, tracked, onView, ...deps]);

  return ref;
};
